import { createContext, useCallback, useContext, useMemo, useState } from "react";
import "../styles/app-feedback.css";

const AppFeedbackContext = createContext(null);

let confirmResolver = null;
let toastId = 0;

export const AppFeedbackProvider = ({ children }) => {
  const [toast, setToast] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const notify = useCallback((message, type = "info", title = "") => {
    const id = ++toastId;

    setToast({
      id,
      message,
      type,
      title
    });

    window.clearTimeout(window.__jootiToastTimer);

    window.__jootiToastTimer = window.setTimeout(() => {
      setToast((current) => (current?.id === id ? null : current));
    }, 4200);
  }, []);

  const confirm = useCallback((options = {}) => {
    const payload = typeof options === "string" ? { message: options } : options;

    setConfirmState({
      title: payload.title || "تأیید عملیات",
      message: payload.message || "آیا از انجام این عملیات مطمئن هستید؟",
      details: payload.details || [],
      confirmText: payload.confirmText || "تأیید",
      cancelText: payload.cancelText || "انصراف",
      variant: payload.variant || "default",
      hideCancel: false
    });

    return new Promise((resolve) => {
      confirmResolver = resolve;
    });
  }, []);

  const showDialog = useCallback((options = {}) => {
    const payload = typeof options === "string" ? { message: options } : options;

    setConfirmState({
      title: payload.title || "پیام جوتی",
      message: payload.message || "",
      details: payload.details || [],
      confirmText: payload.confirmText || "متوجه شدم",
      cancelText: "",
      variant: payload.variant || "info",
      hideCancel: true
    });

    return new Promise((resolve) => {
      confirmResolver = resolve;
    });
  }, []);

  const closeToast = useCallback(() => {
    setToast(null);
  }, []);

  const resolveConfirm = useCallback((value) => {
    if (confirmResolver) {
      confirmResolver(value);
      confirmResolver = null;
    }

    setConfirmState(null);
  }, []);

  const value = useMemo(() => ({
    notify,
    confirm,
    showDialog
  }), [notify, confirm, showDialog]);

  return (
    <AppFeedbackContext.Provider value={value}>
      {children}

      {toast && (
        <div className={`app-toast app-toast-${toast.type}`} role="status">
          <button type="button" onClick={closeToast} aria-label="بستن پیام">
            ×
          </button>

          <div>
            {toast.title && <strong>{toast.title}</strong>}
            <span>{toast.message}</span>
            {["error", "warning"].includes(toast.type) && (
              <small className="app-toast-support">
                نیاز به راهنمایی دارید؟ <a href="/contact">فرم تماس با ما</a> یا <a href="tel:09176427900">09176427900</a>
              </small>
            )}
          </div>
        </div>
      )}

      {confirmState && (
        <div className="app-confirm-backdrop" role="presentation" onMouseDown={() => resolveConfirm(false)}>
          <section className={`app-confirm-card app-confirm-${confirmState.variant}`} role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="app-confirm-glow" />

            <div className="app-confirm-icon">
              !
            </div>

            <h2>{confirmState.title}</h2>
            {confirmState.message && <p>{confirmState.message}</p>}

            {Array.isArray(confirmState.details) && confirmState.details.length > 0 && (
              <div className="app-confirm-details">
                {confirmState.details.map((detail, index) => (
                  <div key={index} className="app-confirm-detail-item">
                    <span>{index + 1}</span>
                    <p>{detail}</p>
                  </div>
                ))}
              </div>
            )}

            <div className={`app-confirm-actions ${confirmState.hideCancel ? "app-confirm-actions-single" : ""}`}>
              {!confirmState.hideCancel && (
                <button type="button" className="app-confirm-cancel" onClick={() => resolveConfirm(false)}>
                  {confirmState.cancelText}
                </button>
              )}

              <button type="button" className="app-confirm-submit" onClick={() => resolveConfirm(true)}>
                {confirmState.confirmText}
              </button>
            </div>
          </section>
        </div>
      )}
    </AppFeedbackContext.Provider>
  );
};

export const useFeedback = () => {
  const context = useContext(AppFeedbackContext);

  if (!context) {
    throw new Error("useFeedback must be used inside AppFeedbackProvider");
  }

  return context;
};
